import React from 'react';
import { RefreshCw, Search, Loader2 } from 'lucide-react';
import { FILTERS } from '@/lib/stockControl';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// The filter bar: category selector, status chips with live counts, search and
// the Square sync trigger. `rows` arrives already narrowed to the selected
// category, so the chip counts always agree with the table underneath them.
export default function StockSummaryBar({
  rows,
  filter,
  setFilter,
  search,
  setSearch,
  categories = [],
  category,
  setCategory,
  total = 0,
  onSync,
  syncing,
  syncedAt,
}) {
  const counts = {
    all: rows.length,
    low: rows.filter((r) => r.isLow || r.isOut).length,
    out: rows.filter((r) => r.isOut).length,
    made_to_order: rows.filter((r) => r.status === 'made_to_order').length,
  };

  const itemCls = 'rounded-none text-[11px] font-bold tracking-widest uppercase focus:bg-primary/15 focus:text-primary';

  return (
    <div className="border border-white/10 bg-[#111115] p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
        {/* Category — narrows the table, and every count beside it, to one group */}
        <div className="flex items-center gap-2.5">
          <span className="text-white/25 text-[10px] font-bold tracking-widest">CATEGORY</span>
          <Select value={category || '__all'} onValueChange={(v) => setCategory(v === '__all' ? '' : v)}>
            <SelectTrigger
              title="Show one category at a time"
              className="w-auto min-w-[190px] h-auto gap-2 rounded-none border border-white/10 bg-transparent px-3 py-2 text-[10px] font-bold tracking-widest uppercase text-white/60 hover:text-white hover:border-white/25 focus:ring-0 focus:ring-offset-0"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none border-white/10 bg-[#111115] text-white/80">
              <SelectItem value="__all" className={itemCls}>All Categories ({total})</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.key} value={c.key} className={itemCls}>
                  {c.label} ({c.count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            const count = counts[f.key] || 0;
            const urgent = count > 0 && (f.key === 'out' || f.key === 'low');
            const urgentCls = f.key === 'out' ? 'bg-red-400/15 text-red-400' : 'bg-amber-400/15 text-amber-400';
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`inline-flex items-center gap-2 px-3 py-2 text-[10px] font-bold tracking-widest border transition-colors ${
                  active
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-white/10 text-white/40 hover:text-white hover:border-white/25'
                }`}
              >
                {f.label.toUpperCase()}
                <span className={`px-1.5 py-0.5 text-[9px] ${active ? 'bg-primary/20' : urgent ? urgentCls : 'bg-white/5'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 ml-auto">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-white/30 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, SKU, variation…"
              className="w-56 bg-transparent border border-white/10 pl-8 pr-3 py-2 text-[11px] text-white placeholder:text-white/25 focus:border-primary/60 focus:outline-none transition-colors"
            />
          </div>
          <button
            onClick={onSync}
            disabled={syncing}
            title="Send the figures on this page up to Square as a physical count, then read Square's confirmed levels back"
            className="inline-flex items-center gap-2 border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black text-cyan-400 font-bold text-[10px] tracking-widest px-4 py-2 transition-colors disabled:opacity-50"
          >
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {syncing ? 'SYNCING…' : 'SYNC WITH SQUARE'}
          </button>
        </div>
      </div>

      {syncedAt && (
        <p className="text-white/25 text-[10px] tracking-widest mt-3">LAST SYNCED AT {syncedAt}</p>
      )}
    </div>
  );
}